import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { CLEAR_ERRORS, FETCH_FEATURE_FLAGS, FETCH_USER, SET_USER } from 'src/store/types';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { email } from 'vee-validate/dist/rules';
import { isValidPassword, MAX_PASSWORD_LENGTH } from 'src/utils/validators';
import { BfSpinner } from 'components/Butterfly';
import { setAuthHeader, deleteAuthHeader } from 'src/utils/auth';
import { getApprovedDomains } from 'src/store/modules/sites/api';
import SubscriptionAPI from 'src/api/subscription';
import AuthAPI from 'src/api/auth';
import SiteAPI from 'src/api/site';
import ProjectAPI from 'src/api/project';
import { PopupCancelledError, PopupTimeoutError } from '@auth0/auth0-spa-js';
import { Auth0 } from 'src/plugins/auth0';
import PasswordChecker from 'pages/PasswordChecker';
import googleIcon from 'assets/icons/google-sso.svg';
import azureADIcon from 'assets/icons/Azure-Active-Directory.svg';
import adfsIcon from 'assets/icons/adfs.svg';
import oktaIcon from 'assets/icons/okta.svg';
import { cloneDeep } from "lodash";
// ================================================================== VALIDATION RULES
// these can be declared in or outside the component
extend('email', email);
extend('validPassword', value => isValidPassword(value));
extend('checked', {
    validate(value) {
        return {
            required: true,
            valid: value === true
        };
    },
    message: 'You must agree to the Terms & Conditions',
    computesRequired: true
});
const UserSignupPage = Vue.extend({
    components: { ValidationObserver, ValidationProvider, BfSpinner, PasswordChecker },
    props: {
        id: { type: String, required: false, default: null },
        signupType: {
            type: String,
            required: true,
            validator: value => ['approved_domain', 'site', 'dashboard'].includes(value)
        },
        sitename: { type: String, required: false, default: null },
        dashboardId: { type: String, required: false, default: null },
        sso: { type: Boolean, required: false, default: false }
    },
    data() {
        return {
            loading: true,
            visibleForms: {
                socialDetails: false,
                socialSelector: true,
                'emailPassword': true,
            },
            showPassword: false,
            domains: null,
            site_id: null,
            exceedsLimits: false,
            checkingLimits: true,
            invitation: null,
            email: '',
            invitationNotFoundError: false,
            doingRegistering: false,
            registrationSuccessful: false,
            firstname: '',
            lastname: '',
            password1: '',
            tandc: false,
            socialUser: null,
            socialProviders: [
                {
                    // Key MUST match the `SiteProfile.LoginMethod` enum in backend
                    key: 'GOOGLE',
                    label: 'Google',
                    icon: googleIcon,
                    connection: 'google-oauth2',
                    subPrefix: 'google-oauth2',
                },
                {
                    // Key MUST match the `SiteProfile.LoginMethod` enum in backend
                    key: 'AZUREAD',
                    label: 'Azure AD',
                    icon: azureADIcon,
                    subPrefix: 'waad',
                    // This must be populated from the Site Profile. The
                    // details will be provided as part of the `invitation`
                    // object that is returned from the backend.
                    connection: undefined,
                },
                {
                    // Key MUST match the `SiteProfile.LoginMethod` enum in backend
                    key: 'ADFS',
                    label: 'Microsoft ADFS',
                    icon: adfsIcon,
                    subPrefix: 'adfs',
                    // This must be populated from the Site Profile. The
                    // details will be provided as part of the `invitation`
                    // object that is returned from the backend.
                    connection: undefined,
                },
                {
                    // Key MUST match the `SiteProfile.LoginMethod` enum in backend
                    key: 'OKTA',
                    label: 'Okta Workforce',
                    icon: oktaIcon,
                    subPrefix: 'okta',
                    // This must be populated from the Site Profile. The
                    // details will be provided as part of the `invitation`
                    // object that is returned from the backend.
                    connection: undefined,
                },
            ],
            errors: [],
            popuperrors: null,
            continueRoute: { name: 'start' },
            socialEmailInApprovedDomain: null,
            socialEmailNotVerified: null,
            socialEmailMatches: null,
            max_password_length: MAX_PASSWORD_LENGTH,
        };
    },
    computed: {
        ...mapGetters(['featureFlags']),
        requiredSignupMethod() {
            return this.invitation?.auth0_signup_method ?? "";
        },
        /**
         * Currently, this returns either "all of them" or "one of them",
         * if the invitation object as received from the backend has
         * specified a required provider. If necessary in the future,
         * this method can easily be changed to return a subset (instead
         * of only one) if the backend specifies more than one.
         */
        enabledSocialProviders() {
            // We don't want any code in this function to mutate the defaults
            let providers = cloneDeep(this.socialProviders);
            if (this.requiredSignupMethod !== "") {
                // If a login method has been configured on this site, allow
                // only that login method for sign-up.
                let required_provider = providers.filter(p => p.key === this.requiredSignupMethod)[0];
                // If the backend has given a specific connection name to use, override
                // the default one for this provider.
                const siteConnectionName = this.invitation?.auth0_connection_name ?? "";
                if (siteConnectionName !== "") {
                    required_provider.connection = siteConnectionName;
                }
                // This function sig always requires a sequence to be returned
                providers = [required_provider];
            }
            return providers;
        },
        showUserInputForm() {
            if (["", "USERPASS"].includes(this.requiredSignupMethod)) {
                return true;
            }
            if (this.visibleForms['socialDetails']) {
                return true;
            }
            return false;
        },
        site() {
            return (this.sitename || this.invitation?.domain);
        },
        signUpMethod() {
            if (!this.useSSO)
                return 'legacy';
            if (this.socialUser)
                return 'social';
            return 'password';
        },
        emailRules() {
            return this.signupType === 'approved_domain' ? 'required|email|inApproved' : 'required|email';
        },
        useSSO() {
            // When launching the auth0 feature, we tested the feature by adding `/sso` to the sign up url. This was a way
            // to covertly test the sign up through auth0 without turning the feature flag on. Now that this feature is GA,
            // we should remove all the machinery related to that route. We've deferred this, that's why it looks odd to have
            // an OR true here.
            return this.sso || true;
        },
        selectedProvider() {
            if (this.auth0LoggedIn && this.socialUser?.sub) {
                const providerLabel = this.socialUser?.sub.split('|')[0];
                return this.socialProviders.find(provider => provider.subPrefix === providerLabel);
            }
            return undefined;
        },
        auth0LoggedIn() {
            return !!this.socialUser;
        },
        inviteUsed() {
            return !!this.invitation?.date_used;
        },
        userType() {
            return this.signupType === 'site' ? this.invitation?.user_type : 'VIEWER';
        }
    },
    async mounted() {
        // we have to declare this rule here because it needs access to the approved domains list
        // don't know if there is a better way.
        extend('inApproved', {
            validate: (value) => this.checkEmailInApprovedDomain(value) ?? false,
            message: 'Email must be from an approved domain.'
        });
        this.loading = true;
        this.clearErrors();
        this.CLEAR_ERRORS();
        try {
            await this.FETCH_FEATURE_FLAGS();
            await this.fetchApprovedDomains();
            await this.getInvitation();
            if (this.site) {
                await this.checkLimits();
            }
        }
        finally {
            this.loading = false;
        }
    },
    methods: {
        ...mapActions({ CLEAR_ERRORS, FETCH_FEATURE_FLAGS }),
        clearErrors() {
            if (Auth0.isAuthenticated) {
                deleteAuthHeader();
                Auth0.logout({ openUrl: false });
            }
            this.errors = [];
            this.popuperrors = null;
            this.socialEmailInApprovedDomain = null;
            this.socialEmailNotVerified = null;
            this.socialEmailMatches = null;
        },
        cancelSocial() {
            this.clearErrors();
            this.socialUser = null;
            this.tandc = false;
            this.visibleForms = {
                socialDetails: false,
                socialSelector: true,
                'emailPassword': true
            };
        },
        checkSocialEmailNotVerified(socialUser) {
            if (socialUser === null)
                return null;
            return !socialUser.email_verified;
        },
        checkSocialEmailMatches(email) {
            if (this.signupType !== 'approved_domain' &&
                this.signUpMethod === 'social' &&
                this.auth0LoggedIn &&
                email !== null) {
                return this.email.toLowerCase() === email.toLowerCase();
            }
            return null;
        },
        checkEmailInApprovedDomain(email) {
            if (email === null)
                return false;
            if (this.signupType !== 'approved_domain')
                return null;
            if (!this.domains)
                return false;
            const parts = email.split('@');
            if (parts.length !== 2)
                return false;
            return !!this.domains?.includes(parts[1]);
        },
        async checkLimits() {
            const exceeds = await SubscriptionAPI.exceedsLimits(this.site);
            this.exceedsLimits = exceeds['exceeds_limits'];
        },
        async fetchApprovedDomains() {
            // if we don't have approved domains then go to the login page instead
            if (this.signupType === 'approved_domain') {
                const domains = await getApprovedDomains(this.sitename);
                if (!domains) {
                    this.$router.push({ name: 'login', query: { next: `/${this.sitename}/dashboards/${this.dashboardId}` } });
                }
                else {
                    this.domains = domains.approved_domains?.map(({ domain }) => domain);
                    this.site_id = domains.site;
                }
            }
        },
        async getInvitation() {
            if (this.id === null)
                return;
            try {
                switch (this.signupType) {
                    case 'site':
                        this.invitation = await SiteAPI.getMembershipInvitation(this.id);
                        break;
                    case 'dashboard':
                        this.invitation = await ProjectAPI.getDashboardInvitation(this.id);
                        break;
                }
                if (this.invitation) {
                    this.email = this.invitation?.email;
                }
            }
            catch (error) {
                if (error.status === 404) {
                    this.invitationNotFoundError = true;
                }
            }
        },
        async doSocialLogin(connection) {
            try {
                this.clearErrors();
                this.socialUser = null;
                /*
                 * 2 minute timeout on login.  After 2 minutes we start the popup
                 * process again.  This resets the popup to the beginning.  If we don't
                 * do this, we can get stuck with a blank unusable popup.
                */
                await Auth0.auth0.checkSession();
                await Auth0.auth0.loginWithPopup({
                    prompt: 'login',
                    connection: connection,
                }, { timeoutInSeconds: 120 });
                this.socialUser = await Auth0.auth0.getUser();
                this.tandc = false;
                this.socialEmailInApprovedDomain = this.checkEmailInApprovedDomain(this.socialUser?.email ?? null);
                this.socialEmailNotVerified = this.checkSocialEmailNotVerified(this.socialUser ?? null);
                this.socialEmailMatches = this.checkSocialEmailMatches(this.socialUser?.email ?? null);
                if (this.socialEmailInApprovedDomain === false
                    || this.socialEmailNotVerified === true
                    || this.socialEmailMatches === false) {
                    await Auth0.logout({ openUrl: false });
                    this.socialUser = null;
                }
            }
            catch (e) {
                if (e instanceof PopupTimeoutError) {
                    await this.doSocialLogin(connection);
                }
                if (!(e instanceof PopupCancelledError)) {
                    this.popuperrors = e.error_description;
                    await Auth0.logout({ openUrl: false });
                    this.socialUser = null;
                }
            }
            finally {
                if (this.socialUser && !this.socialEmailNotVerified && this.socialEmailMatches !== false) {
                    this.visibleForms = {
                        socialDetails: true,
                        socialSelector: false,
                        'emailPassword': false
                    };
                }
            }
        },
        // ===================================================== Account registration Methods
        userForRegistering() {
            switch (this.signUpMethod) {
                case 'social': {
                    if (!this.socialUser)
                        return null;
                    return {
                        auth0User: this.socialUser
                    };
                }
                case 'password': {
                    return {
                        auth0User: {
                            email: this.email,
                            first_name: this.firstname,
                            last_name: this.lastname,
                            password1: this.password1,
                            password2: this.password1
                        }
                    };
                }
                case 'legacy': {
                    return {
                        email: this.email,
                        first_name: this.firstname,
                        last_name: this.lastname,
                        password1: this.password1,
                        password2: this.password1,
                        user_type: 'VIEWER'
                    };
                }
            }
            return null;
        },
        async doRegister() {
            this.doingRegistering = true;
            this.registrationSuccessful = false;
            const user = this.userForRegistering();
            if (!user)
                return;
            this.clearErrors();
            try {
                switch (this.signupType) {
                    case 'approved_domain':
                        if (!this.dashboardId)
                            return;
                        if (!this.site_id)
                            return;
                        let signupUser = {
                            ...user,
                            site_id: this.site_id,
                            dashboard_code: this.dashboardId,
                        };
                        await AuthAPI.register(signupUser, this.useSSO);
                        break;
                    case 'site':
                        if (!this.invitation)
                            return;
                        await SiteAPI.acceptMembershipInvitation(this.invitation.uuid, user, this.useSSO);
                        break;
                    case 'dashboard':
                        if (!this.invitation)
                            return;
                        let response = await ProjectAPI.acceptDashboardInvitation(this.invitation.uuid, user, this.useSSO);
                        if (!this.useSSO) {
                            setAuthHeader({ botanicToken: response?.key });
                            await this.$store.dispatch({ type: FETCH_USER });
                            this.$router.push({
                                name: 'dashboard',
                                params: { dashboardId: response.dashboard_url_code, site: response.site }
                            });
                        }
                        else {
                            const dashboardURL = `/${response.site}/dashboards/${response.dashboard_url_code}`;
                            // If using SSO, the user can be logged in immediately
                            await Auth0.auth0.checkSession();
                            const claims = await Auth0.getIdTokenClaims();
                            const route = {
                                name: 'login',
                                query: {
                                    next: encodeURIComponent(dashboardURL)
                                }
                            };
                            if (claims) {
                                const token = claims.__raw;
                                setAuthHeader({ auth0Token: token, claims });
                                try {
                                    const user = await AuthAPI.getUser();
                                    this.$store.commit(SET_USER, { token, user });
                                    this.$router.push(route);
                                    return;
                                }
                                catch {
                                    deleteAuthHeader();
                                    this.CLEAR_ERRORS();
                                }
                            }
                            this.continueRoute = route;
                        }
                        break;
                }
                this.registrationSuccessful = true;
            }
            catch (errors) {
                // error body is a string
                if (typeof errors.body === 'string') {
                    this.errors.push(errors.body);
                }
                // error body is an array
                else if (Array.isArray(errors.body)) {
                    errors.body.forEach((errorMsg) => {
                        let chunks = errorMsg.split(':');
                        if (chunks.length === 1) {
                            this.errors.push(errorMsg);
                        }
                        if (chunks.length > 1) {
                            if (chunks[0] === 'PasswordStrengthError') {
                                this.$refs["form"]?.setErrors({ 'Password': chunks.slice(1).join() });
                            }
                            else {
                                this.errors.push(errorMsg);
                            }
                        }
                    });
                }
                // error body is an object
                else if (typeof errors.body === 'object') {
                    Object.keys(errors.body).forEach(key => {
                        if (errors.body[key].includes('This password is too common')) {
                            this.$refs.form?.setErrors({ 'Password': 'This password is too common.' });
                        }
                        else {
                            const theError = errors.body[key];
                            if (Array.isArray(theError)) {
                                this.errors.push(theError.join());
                            }
                            else {
                                this.errors.push(theError);
                            }
                        }
                    });
                }
                // error body was something else? unlikely but just in case
                else {
                    this.errors.push('Unexpected error:', JSON.stringify(errors.body, null, 2));
                }
            }
            finally {
                this.doingRegistering = false;
            }
        }
    }
});
export default UserSignupPage;
